import { useState } from 'react';

import { useTranslation } from 'libs/i18n';
import { IconButton } from 'libs/ui';
import { useDialog } from 'libs/ui/Dialog/useDialog';
import { Icon } from 'libs/ui/Icon';
import {
  useFormContext,
  WithdrawalAccountForm,
} from 'modules/client/client/forms/withdrawalAccountsForm';
import { Section } from 'modules/client/common/components';
import { DialogViewWithdrawalAccountFeature } from 'modules/client/common/components/sections/WithdrawalAccountsSection/FormContent/components/DialogViewWithdrawalAccountFeature';

import { FormContent } from './FormContent/FormContent';
import {
  WithdrawalAccountItem,
  DialogAddWithdrawalAccountFeature,
  DialogEditWithdrawalAccountFeature,
} from './FormContent/components';

export const WithdrawalAccountsSection = () => {
  const { t } = useTranslation();
  const { readOnly, setFieldValue, values } = useFormContext();
  const [selectedWithdrawalAccount, setSelectedWithdrawalAccount] =
    useState<WithdrawalAccountItem | null>(null);

  const {
    open: openAddWithdrawalAccountDialog,
    onClose: onCloseAddWithdrawalAccountDialog,
    ...dialogAddWithdrawalAccountDialogProps
  } = useDialog();

  const {
    open: openEditWithdrawalAccountDialog,
    onClose: onCloseEditWithdrawalAccountDialog,
    ...dialogEditWithdrawalAccountDialogProps
  } = useDialog();

  const {
    open: openViewWithdrawalAccountDialog,
    onClose: onCloseViewWithdrawalAccountDialog,
    ...dialogViewWithdrawalAccountDialogProps
  } = useDialog();

  const onAddWithdrawalAccount = (value: WithdrawalAccountForm) => {
    const withdrawalAccounts = [...values.withdrawalAccounts, value];
    setFieldValue('withdrawalAccounts', withdrawalAccounts, false);
    onCloseAddWithdrawalAccountDialog();
  };

  const onRemoveWithdrawalAccount = (row: WithdrawalAccountItem) => {
    const withdrawalAccounts = [...values.withdrawalAccounts];
    withdrawalAccounts.splice(row.index, 1);
    setFieldValue('withdrawalAccounts', withdrawalAccounts, false);
  };

  const onEditWithdrawalAccount = (value: WithdrawalAccountForm) => {
    if (!selectedWithdrawalAccount) {
      return;
    }

    const withdrawalAccounts = [...values.withdrawalAccounts];
    withdrawalAccounts[selectedWithdrawalAccount.index] = value;
    setFieldValue('withdrawalAccounts', withdrawalAccounts, false);

    onCloseEditWithdrawalAccountDialog();
    setSelectedWithdrawalAccount(null);
  };

  const onCellClickWithdrawalAccount = (row: WithdrawalAccountItem) => {
    setSelectedWithdrawalAccount(row);
    if (readOnly) {
      openViewWithdrawalAccountDialog();
      return;
    }
    openEditWithdrawalAccountDialog();
  };

  const onCloseEditWithdrawalAccountDialogHandler = () => {
    onCloseEditWithdrawalAccountDialog();
    setSelectedWithdrawalAccount(null);
  };

  const onCloseViewWithdrawalAccountDialogHandler = () => {
    onCloseViewWithdrawalAccountDialog();
    setSelectedWithdrawalAccount(null);
  };

  return (
    <Section
      datax="withdrawalAccountsSection"
      headerRight={
        !readOnly && (
          <IconButton
            icon={<Icon.Plus size={16} />}
            mode="outlined"
            sizeVariant="xs"
            onClick={openAddWithdrawalAccountDialog}
          />
        )
      }
      title={t('client.client.withdrawalAccounts.title')}
    >
      <FormContent
        onCellClickWithdrawalAccount={onCellClickWithdrawalAccount}
        onRemoveWithdrawalAccount={onRemoveWithdrawalAccount}
      />
      <DialogAddWithdrawalAccountFeature
        onClose={onCloseAddWithdrawalAccountDialog}
        onSubmit={onAddWithdrawalAccount}
        {...dialogAddWithdrawalAccountDialogProps}
      />
      {selectedWithdrawalAccount && (
        <DialogEditWithdrawalAccountFeature
          initialValues={selectedWithdrawalAccount}
          readOnly={readOnly}
          onClose={onCloseEditWithdrawalAccountDialogHandler}
          onSubmit={onEditWithdrawalAccount}
          {...dialogEditWithdrawalAccountDialogProps}
        />
      )}
      {selectedWithdrawalAccount && (
        <DialogViewWithdrawalAccountFeature
          initialValues={selectedWithdrawalAccount}
          readOnly={readOnly}
          onClose={onCloseViewWithdrawalAccountDialogHandler}
          {...dialogViewWithdrawalAccountDialogProps}
        />
      )}
    </Section>
  );
};
